import React, { useCallback, useRef } from "react";

const TASKBAR_HEIGHT = 44;

const Window = ({
  win,
  isActive,
  isMobile,
  onClose,
  onMinimize,
  onToggleMaximize,
  onFocus,
  onMove,
  children,
}) => {
  const dragRef = useRef(null);

  const handlePointerDown = useCallback(
    (event) => {
      onFocus(win.id);
      if (win.maximized || isMobile) return;
      if (event.target.closest("button")) return;
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: win.x,
        originY: win.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [onFocus, win.id, win.maximized, win.x, win.y, isMobile]
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!dragRef.current) return;
      const dx = event.clientX - dragRef.current.startX;
      const dy = event.clientY - dragRef.current.startY;
      const nextX = Math.max(0, dragRef.current.originX + dx);
      const nextY = Math.max(0, dragRef.current.originY + dy);
      onMove(win.id, nextX, nextY);
    },
    [onMove, win.id]
  );

  const handlePointerUp = useCallback((event) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  if (win.minimized) return null;

  const maximized = win.maximized || isMobile;

  const style = maximized
    ? {
        left: 0,
        top: 0,
        right: 0,
        bottom: TASKBAR_HEIGHT,
        width: "auto",
        height: "auto",
        zIndex: win.z,
      }
    : {
        left: win.x,
        top: win.y,
        width: win.width,
        maxWidth: "calc(100vw - 24px)",
        height: win.height,
        maxHeight: `calc(100vh - ${TASKBAR_HEIGHT + 24}px)`,
        zIndex: win.z,
      };

  return (
    <section
      className="win-raised fixed flex flex-col p-[3px] shadow-[6px_6px_0_rgba(0,0,0,0.35)]"
      style={style}
      onPointerDown={() => onFocus(win.id)}
      role="dialog"
      aria-label={win.title}
    >
      <header
        className={`flex select-none items-center justify-between px-1.5 py-1 ${
          isActive ? "win-titlebar-active" : "win-titlebar-inactive"
        }`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={() => !isMobile && onToggleMaximize(win.id)}
      >
        <div className="flex min-w-0 items-center gap-1.5 text-white">
          <span aria-hidden="true" className="text-sm leading-none">{win.icon}</span>
          <span className="truncate text-sm font-bold tracking-tight">{win.title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-[3px]">
          <button
            type="button"
            aria-label="Minimize"
            className="win-btn win-focus-dotted flex h-[19px] w-[21px] items-center justify-center text-xs font-black leading-none"
            onClick={() => onMinimize(win.id)}
          >
            <span className="mt-2">_</span>
          </button>
          {!isMobile && (
            <button
              type="button"
              aria-label={win.maximized ? "Restore" : "Maximize"}
              className="win-btn win-focus-dotted flex h-[19px] w-[21px] items-center justify-center text-[10px] font-black leading-none"
              onClick={() => onToggleMaximize(win.id)}
            >
              {win.maximized ? "❐" : "□"}
            </button>
          )}
          {win.closable && (
            <button
              type="button"
              aria-label="Close"
              className="win-btn win-focus-dotted flex h-[19px] w-[21px] items-center justify-center text-xs font-black leading-none"
              onClick={() => onClose(win.id)}
            >
              ×
            </button>
          )}
        </div>
      </header>
      <div className="win-scroll win-sunken-thin mt-[3px] flex-1 overflow-auto">
        {children}
      </div>
    </section>
  );
};

export default Window;
